"use client";

import { IconMagnifyingGlass } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { IconAlertInfoOutline18, IconCircleCheckOutline18 } from "nucleo-ui-outline-18";
import { Input } from "@/components/ui/input";
import { usePolymarketMarket } from "@/hooks/usePolymarketData";
import { useEffect, useMemo, useRef, useState } from "react";
interface PolymarketOutcomeValue {
  market: string;
  outcome: string;
  tokenId: string;
  groupOutcome?: string; // Sub-market name for multi-market preselection
}
interface PolymarketOutcomeSelectorProps {
  label: string;
  description?: string;
  value: PolymarketOutcomeValue | string;
  onChange: (value: PolymarketOutcomeValue) => void;
  placeholder?: string;
}
export default function PolymarketOutcomeSelector({
  label,
  description,
  value,
  onChange,
  placeholder
}: PolymarketOutcomeSelectorProps) {
  // Parse incoming value (could be object or empty string on first render)
  const parsed: PolymarketOutcomeValue = typeof value === "object" && value !== null ? value : {
    market: "",
    outcome: "",
    tokenId: "",
    groupOutcome: ""
  };
  const [urlInput, setUrlInput] = useState(parsed.market);
  const [submittedUrl, setSubmittedUrl] = useState(parsed.market);
  const [selectedSubMarket, setSelectedSubMarket] = useState<string>("");
  const {
    data: marketData,
    isLoading,
    error: marketError
  } = usePolymarketMarket(submittedUrl);
  const error = marketError?.message || null;
  const activeSubMarket = useMemo(() => {
    if (!marketData?.isMultiMarket || !selectedSubMarket) return undefined;
    return marketData.markets.find(m => m.question === selectedSubMarket);
  }, [marketData, selectedSubMarket]);

  // Outcomes to display: from sub-market if multi, otherwise top-level
  const outcomes = useMemo(() => {
    if (!marketData) return [];
    const source = activeSubMarket || marketData;
    return source.outcomes.map((name, idx) => ({
      name,
      price: parseFloat(source.outcomePrices[idx]),
      tokenId: source.tokenIds[idx]
    }));
  }, [marketData, activeSubMarket]);

  // Auto-select sub-market + outcome from default value when market data loads
  const didAutoSelect = useRef(false);
  useEffect(() => {
    if (!marketData || didAutoSelect.current) return;
    if (marketData.isMultiMarket && !selectedSubMarket) {
      // Try to find sub-market: first by tokenId (exact), then by groupOutcome text (fuzzy)
      let matchingSub = parsed.tokenId ? marketData.markets.find(m_0 => m_0.tokenIds.includes(parsed.tokenId)) : undefined;
      if (!matchingSub && parsed.groupOutcome) {
        const needle = parsed.groupOutcome.toLowerCase();
        matchingSub = marketData.markets.find(m_1 => m_1.groupItemTitle.toLowerCase().includes(needle) || m_1.question.toLowerCase().includes(needle) || needle.includes(m_1.groupItemTitle.toLowerCase()));
      }
      if (matchingSub) {
        setSelectedSubMarket(matchingSub.question);

        // Also resolve outcome + tokenId if we have a matching outcome name
        if (parsed.outcome) {
          const outcomeIdx = matchingSub.outcomes.findIndex(o => o.toLowerCase() === parsed.outcome.toLowerCase());
          if (outcomeIdx >= 0) {
            onChange({
              market: parsed.market,
              outcome: parsed.outcome,
              tokenId: matchingSub.tokenIds[outcomeIdx]
            });
          }
        }
        didAutoSelect.current = true;
      }
    } else if (!marketData.isMultiMarket && parsed.outcome && !parsed.tokenId) {
      // Single-binary with outcome name but no tokenId — resolve the tokenId
      const idx_0 = marketData.outcomes.findIndex(o_0 => o_0.toLowerCase() === parsed.outcome.toLowerCase());
      if (idx_0 >= 0 && marketData.tokenIds[idx_0]) {
        onChange({
          market: parsed.market,
          outcome: parsed.outcome,
          tokenId: marketData.tokenIds[idx_0]
        });
        didAutoSelect.current = true;
      }
    }
  }, [marketData, parsed.tokenId, parsed.outcome, parsed.groupOutcome, parsed.market, selectedSubMarket, onChange]);
  const emitChange = (market: string, outcome: string, tokenId: string) => {
    onChange({
      market,
      outcome,
      tokenId
    });
  };
  const handleFetch = () => {
    if (urlInput.trim()) {
      setSubmittedUrl(urlInput.trim());
      setSelectedSubMarket("");
      didAutoSelect.current = false;
      emitChange(urlInput.trim(), "", "");
    }
  };
  const formatPrice = (price: number) => `${Math.round(price * 100)}¢`;
  return <div className="min-w-0 space-y-3">
      {label && <label className="field-label block">{label}</label>}
      {description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>}
      {/* URL Input + Fetch */}
      <div className="flex min-w-0 gap-2">
        <Input type="text" value={urlInput} onChange={e => setUrlInput(e.target.value)} onKeyDown={e_0 => {
        if (e_0.key === "Enter") {
          e_0.preventDefault();
          handleFetch();
        }
      }} placeholder={placeholder || "https://polymarket.com/event/..."} className="text-ds-text-primary min-w-0 flex-1" />
        <button type="button" onClick={handleFetch} disabled={isLoading || !urlInput.trim()} className="border-ds-neutral-300 text-ds-text-secondary bg-ds-surface-card hover:bg-ds-surface-hover flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium disabled:opacity-50 dark:border-zinc-600">
          <IconMagnifyingGlass className="h-4 w-4" />
          {isLoading ? "..." : "Fetch"}
        </button>
      </div>
      {/* Error */}
      {error && <p className="flex items-center gap-1 text-sm text-red-500">
          <IconAlertInfoOutline18 className="h-4 w-4" /> {error}
        </p>}
      {/* Market loaded */}
      {marketData && <>
          <p className="flex items-center gap-1 text-sm text-green-600 dark:text-green-400">
            <IconCircleCheckOutline18 className="h-4 w-4" /> {marketData.title}
          </p>

          {marketData.marketType === "neg-risk" || marketData.marketType === "grouped-binary" ? (/* Multi-market: scrollable list of sub-markets + Yes/No outcome buttons */
      <div className="space-y-2">
              <label className="field-label-muted">Step 1: Select Market</label>
              <div className="max-h-48 space-y-1.5 overflow-y-auto pr-1">
                {marketData.markets.map(sub => {
            const yesPrice = parseFloat(sub.outcomePrices[0]);
            const isSelected = selectedSubMarket === sub.question;
            const displayLabel = sub.groupItemTitle || sub.question;
            return <button key={sub.id} type="button" onClick={() => {
              setSelectedSubMarket(sub.question);
              emitChange(submittedUrl, "", "");
            }} className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm transition-all ${isSelected ? "bg-blue-600 text-white ring-2 ring-blue-400" : "bg-ds-neutral-100 text-ds-text-secondary hover:bg-ds-neutral-200 dark:bg-ds-neutral-700/50 dark:hover:bg-ds-neutral-700"}`}>
                      <span className="mr-2 flex-1 truncate">{displayLabel}</span>
                      <span className={`shrink-0 font-semibold ${isSelected ? "text-white" : "text-green-600 dark:text-green-400"}`}>
                        {formatPrice(yesPrice)}
                      </span>
                    </button>;
          })}
              </div>
              {/* Hint when no sub-market selected */}
              {!selectedSubMarket && <p className="label-xs">Pick a market above to see outcomes</p>}
              {/* Outcome buttons after sub-market selected */}
              {outcomes.length > 0 && activeSubMarket && <div className={`space-y-2 rounded-lg p-2 ${!parsed.outcome ? "ring-2 ring-blue-400 ring-offset-1 dark:ring-blue-500 dark:ring-offset-zinc-900" : ""}`}>
                  <label className="field-label-muted">Step 2: Select Outcome</label>
                  {!parsed.outcome && <p className="text-xs text-blue-600 dark:text-blue-400">Select an outcome below to continue</p>}
                  <div className="grid grid-cols-2 gap-2">
                    {outcomes.map((outcome_0, idx_1) => {
              const isSelected_0 = parsed.outcome === outcome_0.name;
              return <button key={outcome_0.name} type="button" onClick={() => emitChange(submittedUrl, outcome_0.name, outcome_0.tokenId)} className={`rounded-lg px-4 py-2.5 text-center font-semibold transition-all ${isSelected_0 ? outcome_0.name.toLowerCase() === "yes" ? "bg-green-600 text-white ring-2 ring-green-400" : "bg-red-500 text-white ring-2 ring-red-400" : "bg-ds-neutral-100 text-ds-text-secondary hover:bg-ds-neutral-200 dark:bg-ds-neutral-700/50 dark:hover:bg-ds-neutral-700"}`}>
                          {outcome_0.name} <span className="ml-1">{formatPrice(outcome_0.price)}</span>
                        </button>;
            })}
                  </div>
                </div>}
            </div>) : (/* Single binary: outcome buttons */
      outcomes.length > 0 && <div className="space-y-2">
                <label className="field-label-muted">Select Outcome</label>
                <div className="grid grid-cols-2 gap-2">
                  {outcomes.map(outcome_1 => {
            const isSelected_1 = parsed.outcome === outcome_1.name;
            return <button key={outcome_1.name} type="button" onClick={() => emitChange(submittedUrl, outcome_1.name, outcome_1.tokenId)} className={`rounded-lg px-4 py-2.5 text-center font-semibold transition-all ${isSelected_1 ? outcome_1.name.toLowerCase() === "yes" ? "bg-green-600 text-white ring-2 ring-green-400" : "bg-red-500 text-white ring-2 ring-red-400" : "bg-ds-neutral-100 text-ds-text-secondary hover:bg-ds-neutral-200 dark:bg-ds-neutral-700/50 dark:hover:bg-ds-neutral-700"}`}>
                        {outcome_1.name} <span className="ml-1">{formatPrice(outcome_1.price)}</span>
                      </button>;
          })}
                </div>
              </div>)}
        </>}
    </div>;
}